"use client";

import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AUTH_SERVICE_UNAVAILABLE_MESSAGE, isSupabaseTransportError } from "@/lib/auth/public-auth";
import Link from "next/link";
import { useState } from "react";
import { Icons } from "@/components/ui/icons"; // Assuming this exists or I'll swap for lucide

export function LoginForm({
  redirectTo,
  inviteToken,
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div"> & { redirectTo?: string; inviteToken?: string }) {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const supabase = createClient();
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;

      if (data.session) {
        const response = await fetch("/auth/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ event: "SIGNED_IN", session: data.session }),
        });

        if (!response.ok) {
          throw new Error("Failed to persist Supabase session");
        }
      }

      const safeRedirect =
        redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//")
          ? redirectTo
          : "/protected";

      router.push(safeRedirect);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An error occurred";
      const normalized = message.toLowerCase();

      if (isSupabaseTransportError(error)) {
        setError(AUTH_SERVICE_UNAVAILABLE_MESSAGE);
      } else if (normalized.includes("email not confirmed")) {
        setError("Please confirm your email address before signing in. Check your inbox for a confirmation link.");
      } else if (normalized.includes("invalid") || normalized.includes("credentials")) {
        setError("Invalid email or password. Please try again.");
      } else if (normalized.includes("persist")) {
        setError("We signed you in, but could not save the session. Please try again.");
      } else {
        setError("Unable to sign in right now. Please try again.");
      }
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("grid gap-6", className)} data-testid="login-form" {...props}>
      <form onSubmit={handleLogin}>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                href="/auth/forgot-password"
                className="text-sm text-muted-foreground underline-offset-4 hover:underline hover:text-primary"
                >
                Forgot password?
                </Link>
            </div>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
            />
          </div>
          {error && (
            <p className="text-sm text-destructive font-medium" data-testid="login-error">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-submit">
            {isLoading && (
              <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            )}
            Sign In
          </Button>
        </div>
      </form>

      <div className="text-center text-sm">
          Don&apos;t have an account?{" "}
          <Link
            href={
              inviteToken
                ? `/auth/signup?next=${encodeURIComponent(`/invite/${inviteToken}`)}`
                : redirectTo
                  ? `/auth/signup?next=${encodeURIComponent(redirectTo)}`
                  : "/auth/signup"
            }
            className="underline underline-offset-4 hover:text-primary"
          >
            Sign up
          </Link>
      </div>
    </div>
  );
}
